import { t } from 'src/i18n';
import type { TranslatableFieldType } from 'src/services/translation-types';

/**
 * Declares which free-text / HTML fields are AI-translatable for each entity
 * type. The `key` is also the property name read from the entity view model.
 * Norwegian is the authoritative source; empty fields are skipped at runtime.
 *
 * Entities whose `name` is a descriptive title include it; entities whose name
 * is a proper noun (people, companies) translate only their free-text fields.
 */
interface TranslatableFieldDef {
  key: string;
  label: string;
  type: TranslatableFieldType;
}

/**
 * Builds the registry on each call so that field labels are resolved through
 * `t()` in the active language at use time, rather than once at module import.
 */
export function getTranslatableFields(): Record<string, TranslatableFieldDef[]> {
  const NAME: TranslatableFieldDef = { key: 'name', label: t('translation.fieldLabel.name'), type: 'text' };
  const CONTENT: TranslatableFieldDef = { key: 'content', label: t('translation.fieldLabel.content'), type: 'html' };

  return {
    documents: [NAME, CONTENT],
    guidelines: [NAME, CONTENT],
    constitutionalDocuments: [NAME, CONTENT],
    contracts: [NAME, CONTENT],
    reports: [
      NAME,
      CONTENT,
      { key: 'formSubtitle', label: t('translation.fieldLabel.subtitle'), type: 'text' },
      { key: 'formPrompt', label: t('translation.fieldLabel.intro'), type: 'text' },
    ],
    functions: [NAME, { key: 'description', label: t('translation.fieldLabel.description'), type: 'html' }],
    tasks: [
      NAME,
      { key: 'situation', label: t('translation.fieldLabel.situation'), type: 'text' },
      { key: 'procedures', label: t('translation.fieldLabel.procedures'), type: 'html' },
    ],
    eventOccurrences: [
      NAME,
      { key: 'procedures', label: t('translation.fieldLabel.procedures'), type: 'html' },
      { key: 'notes', label: t('translation.fieldLabel.notes'), type: 'text' },
    ],
    meetingOccurrences: [
      NAME,
      { key: 'meetingAgenda', label: t('translation.fieldLabel.agenda'), type: 'html' },
      { key: 'notes', label: t('translation.fieldLabel.notes'), type: 'text' },
    ],
    riskAssessments: [
      NAME,
      { key: 'description', label: t('translation.fieldLabel.description'), type: 'html' },
      { key: 'riskDescription', label: t('translation.fieldLabel.consequence'), type: 'text' },
      { key: 'probabilityDescription', label: t('translation.fieldLabel.probability'), type: 'text' },
      { key: 'measures', label: t('translation.fieldLabel.measures'), type: 'text' },
    ],
    issues: [
      NAME,
      { key: 'description', label: t('translation.fieldLabel.description'), type: 'text' },
      { key: 'immediateMeasures', label: t('translation.fieldLabel.immediateMeasures'), type: 'text' },
      { key: 'furtherMeasures', label: t('translation.fieldLabel.furtherMeasures'), type: 'text' },
      { key: 'identifiedMeasures', label: t('translation.fieldLabel.identifiedMeasures'), type: 'text' },
      { key: 'notes', label: t('translation.fieldLabel.notes'), type: 'text' },
      {
        key: 'workRelatedHealthIssueDescription',
        label: t('translation.fieldLabel.healthIssueDescription'),
        type: 'text',
      },
      { key: 'workRelatedInjuryTask', label: t('translation.fieldLabel.injuryTask'), type: 'text' },
      { key: 'workRelatedInjuryIncidentCause', label: t('translation.fieldLabel.injuryIncidentCause'), type: 'text' },
      { key: 'workRelatedSicknessCausingTask', label: t('translation.fieldLabel.sicknessCausingTask'), type: 'text' },
      {
        key: 'workRelatedSicknessCausingTaskLocation',
        label: t('translation.fieldLabel.sicknessCausingTaskLocation'),
        type: 'text',
      },
      {
        key: 'workRelatedSicknessCausingEnvironment',
        label: t('translation.fieldLabel.sicknessCausingEnvironment'),
        type: 'text',
      },
    ],
    substances: [
      NAME,
      { key: 'applications', label: t('translation.fieldLabel.applications'), type: 'text' },
      { key: 'location', label: t('translation.fieldLabel.location'), type: 'text' },
      { key: 'notes', label: t('translation.fieldLabel.notes'), type: 'text' },
    ],
    collections: [NAME, { key: 'description', label: t('translation.fieldLabel.description'), type: 'text' }],
    // Entities whose name is a proper noun: translate only free-text fields.
    employees: [{ key: 'notes', label: t('translation.fieldLabel.notes'), type: 'text' }],
    partners: [{ key: 'notes', label: t('translation.fieldLabel.notes'), type: 'text' }],
    contacts: [{ key: 'notes', label: t('translation.fieldLabel.notes'), type: 'text' }],
  };
}
